import React, { useEffect, useCallback, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import Navbar from '../components/Navbar';
import AccessModal from '../components/AccessModal';
import InsufficientTokensModal from '../components/InsufficientTokensModal';
import LoadingTokensModal from '../components/LoadingTokensModal';
import SimpleLoadingModal from '../components/SimpleLoadingModal';
import VipModal from '../components/VipModal';
import PaymentModal from '../components/PaymentModal';
import UpsellCard from '../components/UpsellCard';
import TokenBalanceCard from '../components/TokenBalanceCard';
import { trackViewContent, trackInitiateCheckout, trackAddToCart, trackPurchase } from '../utils/metaPixel';
import NSFWBadge from '../components/NSFWBadge';
import Button from '../components/ui/Button';
import { grantPrivateShowAccess } from './PrivateShow';

const PRIVATE_SHOW_PACKAGE = {
  id: 'private_show_24h',
  type: 'vip',
  title: 'Show particular 24h',
  price: 29.9,
  tokens: null,
  product_hash: 'vip_upgrade',
};

const Dashboard = () => {
  const { user, refreshUser, isLoggedIn, loading, updateUser } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [showVipModal, setShowVipModal] = useState(false);
  const [showUpsellPayment, setShowUpsellPayment] = useState(false);
  const [showAccessModal, setShowAccessModal] = useState(false);
  const [showInsufficientTokensModal, setShowInsufficientTokensModal] = useState(false);
  const [showLoadingTokensModal, setShowLoadingTokensModal] = useState(false);
  const [showSimpleModal, setShowSimpleModal] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);

  const handleUpsellCta = () => {
    if (!isLoggedIn) {
      setSelectedCategory({ title: 'Show particular 24h' });
      setShowAccessModal(true);
      return;
    }
    // Já tem acesso → vai direto pro show
    if (user?.isVip) {
      navigate('/ao-vivo');
      return;
    }
    trackViewContent('private_show_24h', 'product', {
      content_name: 'Show particular 24h',
      value: PRIVATE_SHOW_PACKAGE.price,
      currency: 'BRL',
    });
    trackInitiateCheckout(PRIVATE_SHOW_PACKAGE.price, 'BRL', ['private_show_24h']);
    trackAddToCart(PRIVATE_SHOW_PACKAGE.price, 'BRL', 'private_show_24h');
    setShowUpsellPayment(true);
  };

  const handleUpsellSuccess = async () => {
    trackPurchase(PRIVATE_SHOW_PACKAGE.price, 'BRL', ['private_show_24h']);
    grantPrivateShowAccess(24);
    updateUser({ isVip: true });
    try {
      await refreshUser();
    } catch {
      /* ignore */
    }
    setShowUpsellPayment(false);
    navigate('/ao-vivo');
  };

  const refreshUserData = useCallback(async () => {
    if (!isLoggedIn) return;
    try {
      await refreshUser();
    } catch (error) {
      console.error('Erro ao atualizar dados do usuário:', error);
    }
  }, [refreshUser, isLoggedIn]);

  const handleCategoryClick = (category) => {
    if (category.requiresLogin && !isLoggedIn) {
      setSelectedCategory(category);
      setShowAccessModal(true);
      return;
    }

    if (category.requiresLogin && isLoggedIn) {
      trackViewContent(category.id, 'product', {
        content_name: category.title,
      });

      if (user.tokens < 25) {
        setShowInsufficientTokensModal(true);
        return;
      }

      setShowVipModal(true);
      return;
    }

    trackViewContent(category.id, 'product', {
      content_name: category.title,
    });
    category.action();
  };

  // ViewContent 1x por visita à página (não re-dispara em refresh/params)
  useEffect(() => {
    trackViewContent('dashboard', 'page', { content_name: 'Dashboard' });
  }, []);

  useEffect(() => {
    if (searchParams.get('vip') === 'true') {
      setShowVipModal(true);
      const next = new URLSearchParams(searchParams);
      next.delete('vip');
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    if (!isLoggedIn) return;
    refreshUserData();
    const interval = setInterval(refreshUserData, 30000);
    return () => clearInterval(interval);
  }, [isLoggedIn, refreshUserData]);

  const categories = [
    {
      id: 1,
      title: 'Trocar roupa',
      description: 'Experimente roupas virtualmente',
      image: `${process.env.PUBLIC_URL}/img/MainImage.webp`,
      requiresLogin: false,
      action: () => navigate('/upload'),
    },
    {
      id: 2,
      title: 'Despir',
      description: 'Remover roupas das fotos',
      image: `${process.env.PUBLIC_URL}/img/1.jpeg`,
      requiresLogin: true,
      action: () => navigate('/undress'),
    },
    {
      id: 3,
      title: 'Ações sexuais e fluidos',
      description: 'Conteúdo adulto avançado',
      image: `${process.env.PUBLIC_URL}/img/2.jpeg`,
      requiresLogin: true,
      action: () => navigate('/upload'),
    },
    {
      id: 4,
      title: 'Despir Animar',
      description: 'Animações de despir',
      video: `${process.env.PUBLIC_URL}/img/3.mp4`,
      requiresLogin: true,
      action: () => navigate('/upload'),
    },
    {
      id: 5,
      title: 'Posições Diversas',
      description: 'Diferentes posições e poses',
      video: `${process.env.PUBLIC_URL}/img/4.mp4`,
      requiresLogin: true,
      action: () => navigate('/upload'),
    },
  ];

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-zinc-950">
        <div className="text-center">
          <Loader2 className="mx-auto mb-4 h-10 w-10 animate-spin text-rose-500" />
          <p className="text-sm text-zinc-400">Carregando...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-950">
      <Navbar />

      <div className="mx-auto max-w-2xl px-4 pb-10 pt-20">
        {!isLoggedIn && (
          <div className="mb-6 rounded-2xl border border-white/10 bg-gradient-to-br from-rose-600 to-rose-700 p-5 text-center text-white">
            <h2 className="text-lg font-semibold">Bem-vindo ao Borracha de Roupa</h2>
            <p className="mx-auto mt-1.5 max-w-sm text-sm text-rose-100">
              Explore nossa plataforma de IA para experimentar roupas virtualmente
            </p>
            <div className="mt-4 flex justify-center gap-2">
              <Button
                onClick={() => navigate('/register')}
                variant="secondary"
                className="!bg-white !text-rose-700 hover:!bg-rose-50 !border-0"
              >
                Criar conta
              </Button>
              <Button
                onClick={() => navigate('/login')}
                variant="secondary"
                className="!bg-white/15 !text-white hover:!bg-white/25 !border-white/20"
              >
                Fazer login
              </Button>
            </div>
          </div>
        )}

        {/* Card de tokens */}
        <TokenBalanceCard
          tokens={user?.tokens || 0}
          isVip={user?.isVip}
          onBuy={() => setShowSimpleModal(true)}
          onVip={() => setShowVipModal(true)}
        />

        {/* Upsell — Show particular 24h */}
        <UpsellCard
          alreadyOwned={Boolean(user?.isVip)}
          onCta={handleUpsellCta}
        />

        {/* Card em destaque */}
        <button
          type="button"
          onClick={() => handleCategoryClick(categories[0])}
          className="group mb-6 block w-full overflow-hidden rounded-2xl border border-white/10 bg-zinc-900 text-left transition-colors hover:border-rose-500/40"
        >
          <div className="relative h-72 overflow-hidden bg-zinc-800">
            <img
              src={categories[0].image}
              alt={categories[0].title}
              className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent" />
            <span className="absolute right-3 top-3 rounded-md bg-zinc-950/80 px-2 py-0.5 text-[11px] font-semibold text-white ring-1 ring-white/20">
              18+
            </span>
            <span className="absolute left-3 top-3 rounded-full bg-amber-400 px-3 py-1 text-xs font-bold text-zinc-900">
              Popular
            </span>
            <div className="absolute bottom-0 left-0 right-0 p-4">
              <h3 className="text-lg font-semibold text-white">{categories[0].title}</h3>
              <p className="text-sm text-zinc-300">{categories[0].description}</p>
            </div>
          </div>
        </button>

        {/* Grid de categorias */}
        <div className="grid grid-cols-2 gap-4">
          {categories.slice(1).map((category) => (
            <button
              type="button"
              key={category.id}
              onClick={() => handleCategoryClick(category)}
              className="group overflow-hidden rounded-2xl border border-white/10 bg-zinc-900 text-left transition-colors hover:border-rose-500/40"
            >
              <div className="relative h-32 overflow-hidden bg-zinc-800">
                <NSFWBadge />
                {category.image ? (
                  <img
                    src={category.image}
                    alt={category.title}
                    className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
                  />
                ) : category.video ? (
                  <video
                    src={category.video}
                    className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
                    autoPlay
                    loop
                    muted
                    playsInline
                  />
                ) : null}
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
              </div>
              <div className="p-3">
                <h3 className="text-sm font-semibold text-white">{category.title}</h3>
                <p className="mt-0.5 text-xs text-zinc-400">{category.description}</p>
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Modal VIP */}
      <VipModal isOpen={showVipModal} onClose={() => setShowVipModal(false)} />

      <PaymentModal
        isOpen={showUpsellPayment}
        onClose={() => setShowUpsellPayment(false)}
        packageData={PRIVATE_SHOW_PACKAGE}
        onSuccess={handleUpsellSuccess}
      />

      <AccessModal
        isOpen={showAccessModal}
        onClose={() => {
          setShowAccessModal(false);
          setSelectedCategory(null);
        }}
        title="Acesso necessário"
        message={`Você precisa fazer login para acessar "${selectedCategory?.title}"`}
        redirectTo="/login"
      />

      <InsufficientTokensModal
        isOpen={showInsufficientTokensModal}
        onClose={() => setShowInsufficientTokensModal(false)}
        requiredTokens={25}
        currentTokens={user?.tokens || 0}
      />

      <LoadingTokensModal
        isOpen={showLoadingTokensModal}
        onClose={() => setShowLoadingTokensModal(false)}
      />

      <SimpleLoadingModal isOpen={showSimpleModal} onClose={() => setShowSimpleModal(false)} />
    </div>
  );
};

export default Dashboard;
